/**
 * /portal/usage — pure usage analytics (no wallet/billing controls).
 * Billing actions live in /portal/orders and /portal/pricing now.
 */
import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  BarChart, Bar, PieChart, Pie, Cell, ResponsiveContainer,
  Tooltip, XAxis, YAxis, Legend,
} from 'recharts';
import { Zap, MessageSquare, BarChart3, Layers } from 'lucide-react';
import { billingApi } from '../../services/billing';
import { portalAnalyticsApi, portalDashboardApi } from '../../services/api';
import { toast } from 'sonner';
import { NexusCard } from '../../components/nexus-ui';
import { engineTier } from '../../lib/modelLabel';

const MODEL_COLORS = {
  'claude-opus-4-6': '#6BA6F5',
  'claude-opus-4-7': '#F26B1F',
  'claude-opus-4-8': '#D946EF',
  unknown:           '#718096',
};

const PortalUsage = () => {
  const [summary, setSummary] = useState(null);
  const [stats, setStats] = useState(null);
  const [txs, setTxs] = useState([]);
  const [filter, setFilter] = useState('consumption');
  const [period, setPeriod] = useState(30);

  const load = async () => {
    try {
      const [s, st, t] = await Promise.all([
        billingApi.usageSummary(period),
        portalDashboardApi.getStats(),
        billingApi.transactions({ limit: 100 }),
      ]);
      setSummary(s.data);
      setStats(st.data);
      setTxs(t.data.items);
    } catch {
      toast.error('Failed to load usage data');
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [period]);

  if (!summary) return <div className="text-nexus-text-tertiary">Loading…</div>;

  const filteredTx = filter ? txs.filter((t) => t.type === filter) : txs;
  const byDay = summary?.by_day || [];
  const byModel = summary?.by_model || [];
  const models = [...new Set(byDay.flatMap((d) => Object.keys(d || {}).filter((k) => k !== 'day')))];
  const hasChartData = byDay.length > 0 && models.length > 0;
  const hasPieData = byModel.length > 0 && byModel.some((m) => m.credits > 0);

  // Aggregates
  const totalTokens = stats?.total_tokens || 0;
  const totalCalls = stats?.total_api_calls || 0;
  const totalConvos = stats?.total_conversations || 0;
  const periodCredits = byModel.reduce((s, m) => s + (m.credits || 0), 0);

  return (
    <div className="space-y-6" data-testid="portal-usage">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] font-semibold tracking-[0.18em] uppercase" style={{ color: '#F26B1F' }}>USAGE</div>
          <h1 className="font-heading text-3xl font-bold text-nexus-text-primary mt-1">Usage analytics</h1>
          <p className="text-[13px] text-nexus-text-secondary mt-1">
            Tokens, credits, conversations and per-model breakdown. Manage credits in <a href="/portal/orders" className="underline hover:text-nexus-text-primary">Orders</a>.
          </p>
        </div>
        <div className="flex gap-1 p-1 rounded-lg" style={{ background: 'var(--layer-2)' }}>
          {[7, 30, 90].map((d) => (
            <button key={d} onClick={() => setPeriod(d)} data-testid={`usage-range-${d}`}
              className="px-3 h-8 rounded text-[12px] transition-all"
              style={{
                background: period === d ? 'var(--layer-0)' : 'transparent',
                color: period === d ? 'var(--nexus-text-primary)' : 'var(--nexus-text-tertiary)',
                fontWeight: period === d ? 600 : 400,
              }}>
              {d}d
            </button>
          ))}
        </div>
      </div>

      {/* Aggregate tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Tokens (period)',     value: totalTokens.toLocaleString(),                                icon: Zap,         accent: '#F26B1F' },
          { label: 'Credits consumed',    value: periodCredits.toFixed(1),                                    icon: Layers,      accent: '#10B981' },
          { label: 'Conversations',       value: totalConvos.toLocaleString(),                                icon: MessageSquare, accent: '#2E70AD' },
          { label: 'API calls',           value: totalCalls.toLocaleString(),                                 icon: BarChart3,    accent: '#92580A' },
        ].map((t, i) => {
          const Icon = t.icon;
          return (
            <motion.div key={t.label}
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              className="rounded-xl p-4"
              style={{ background: 'var(--layer-1)', border: '1px solid var(--hairline)' }}
              data-testid={`usage-tile-${t.label.toLowerCase().split(' ')[0]}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center"
                  style={{ background: `${t.accent}15`, border: `1px solid ${t.accent}30` }}>
                  <Icon size={15} style={{ color: t.accent }} />
                </div>
              </div>
              <div className="text-[10px] font-semibold tracking-wider uppercase text-nexus-text-tertiary">{t.label}</div>
              <div className="font-heading text-[28px] font-bold text-nexus-text-primary leading-tight mt-1">
                {t.value}
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Usage chart */}
      <div className="grid grid-cols-1 lg:grid-cols-[2fr,1fr] gap-4">
        <NexusCard variant="glass" hover={false}>
          <div className="p-5">
            <div className="text-[10px] font-semibold tracking-wider uppercase mb-3" style={{ color: '#F26B1F' }}>
              LAST {period} DAYS
            </div>
            <div style={{ height: 280 }}>
              {hasChartData ? (
                <ResponsiveContainer>
                  <BarChart data={byDay}>
                    <XAxis dataKey="day" stroke="#718096" style={{ fontSize: 10 }} />
                    <YAxis stroke="#718096" style={{ fontSize: 10 }} />
                    <Tooltip contentStyle={{ background: '#0A1B2E', border: '1px solid #F26B1F', borderRadius: 8, color: '#fff', fontSize: 11 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {models.map((m) => <Bar key={m} dataKey={m} name={engineTier(m)} stackId="a" fill={MODEL_COLORS[m] || '#718096'} />)}
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-nexus-text-tertiary text-[12px]">
                  No usage yet — start chatting to see trends here.
                </div>
              )}
            </div>
          </div>
        </NexusCard>
        <NexusCard variant="glass" hover={false}>
          <div className="p-5">
            <div className="text-[10px] font-semibold tracking-wider uppercase mb-3" style={{ color: '#F26B1F' }}>BY MODEL</div>
            <div style={{ height: 280 }}>
              {hasPieData ? (
                <ResponsiveContainer>
                  <PieChart>
                    <Pie dataKey="credits" data={byModel.map((e) => ({ ...e, name: engineTier(e.model) }))} nameKey="name"
                      cx="50%" cy="50%" innerRadius={50} outerRadius={90} paddingAngle={2}>
                      {byModel.map((e, i) => <Cell key={i} fill={MODEL_COLORS[e.model] || '#718096'} />)}
                    </Pie>
                    <Tooltip contentStyle={{ background: '#0A1B2E', border: '1px solid #F26B1F', borderRadius: 8, color: '#fff', fontSize: 11 }} />
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-nexus-text-tertiary text-[12px]">—</div>
              )}
            </div>
          </div>
        </NexusCard>
      </div>

      {/* Transactions */}
      <NexusCard variant="glass" hover={false}>
        <div className="p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] font-semibold tracking-wider uppercase" style={{ color: '#F26B1F' }}>USAGE LEDGER</div>
            <select value={filter} onChange={(e) => setFilter(e.target.value)}
              className="text-[11px] px-2 py-1 rounded-md text-nexus-text-primary"
              style={{ background: 'var(--layer-0)', border: '1px solid var(--hairline)' }}
              data-testid="usage-filter">
              <option value="">All</option>
              <option value="consumption">Consumption</option>
              <option value="purchase">Purchases</option>
              <option value="free_grant">Free grant</option>
              <option value="auto_topup">Auto top-up</option>
            </select>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead style={{ color: '#718096' }}>
                <tr>
                  <th className="text-left px-3 py-2 font-semibold">Date</th>
                  <th className="text-left px-3 py-2 font-semibold">Type</th>
                  <th className="text-right px-3 py-2 font-semibold">Credits</th>
                  <th className="text-left px-3 py-2 font-semibold">Model</th>
                  <th className="text-right px-3 py-2 font-semibold">Tokens</th>
                  <th className="text-right px-3 py-2 font-semibold">Balance</th>
                </tr>
              </thead>
              <tbody>
                {filteredTx.map((t, i) => (
                  <tr key={t.id || i} className="border-b" style={{ borderColor: 'var(--hairline)' }}
                    data-testid={`usage-row-${i}`}>
                    <td className="px-3 py-2 text-nexus-text-secondary">{t.created_at?.slice(0, 16).replace('T', ' ')}</td>
                    <td className="px-3 py-2 capitalize">
                      <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold"
                        style={{
                          background: t.credits > 0 ? 'rgba(110,231,183,0.12)' : 'rgba(242,107,31,0.12)',
                          color: t.credits > 0 ? '#10B981' : '#F26B1F',
                        }}>
                        {t.type}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right font-mono"
                      style={{ color: t.credits > 0 ? '#10B981' : '#F26B1F' }}>
                      {t.credits > 0 ? '+' : ''}{Number(t.credits).toFixed(2)}
                    </td>
                    <td className="px-3 py-2 text-nexus-text-tertiary text-[11px]">{t.model ? engineTier(t.model) : '—'}</td>
                    <td className="px-3 py-2 text-right font-mono text-nexus-text-tertiary text-[11px]">
                      {t.tokens_total ? t.tokens_total.toLocaleString() : '—'}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-nexus-text-primary">
                      {t.balance_after != null ? Number(t.balance_after).toFixed(1) : '—'}
                    </td>
                  </tr>
                ))}
                {filteredTx.length === 0 && (
                  <tr><td colSpan="6" className="text-center py-6 text-nexus-text-tertiary">No activity yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </NexusCard>
    </div>
  );
};

export default PortalUsage;
